import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors
} from '@angular/forms';
import { HttpClient } from '@angular/common/http';

import { switchMap, finalize, forkJoin, of, catchError } from 'rxjs';
import { environment } from '../../../../environments/environment';

import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';

import { BusinessApiService } from '../services/business-api.service';
import {
  BusinessAdminInvitation,
  BusinessRequest,
  BusinessResponse,
  IDENTIFICATION_TYPES
} from '../models/business.model';
import { ProductoApiService } from '../../productos/services/producto-api.service';
import { CurrencyResponse } from '../../productos/models/producto.model';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { ToastService } from '../../../shared/services/toast.service';

// import {
//   AuthApiService,
//   BackendRegisterRequest,
//   AdminUpdateUserRequest,
//   UserResponse
// } from '../../auth-pages/services/auth-api.service';
import { BusinessAdminAuthService } from '../services/business-admin-auth.service';
import {
  AdminUpdateUserRequest,
  BusinessAdminUserResponse
} from '../models/business-admin-user.model';
import { InvitationApiService } from '../../invitations/services/invitation-api.service';


@Component({
  selector: 'app-business-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent
  ],
  templateUrl: './business-form.component.html'
})
export class BusinessFormComponent implements OnInit {
  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  businessId: number | null = null;

  savingStep: 'idle' | 'business' | 'admin' = 'idle';

  adminUserId: number | null = null;
  adminExists = false;
  /** Rol del admin vinculado existente (1=Super Admin, 2=Admin). Se preserva al actualizar. */
  adminRoleId: number | null = null;

  /**
   * INC 15 — invitación del administrador (GET /api/businesses/{id}/admin-invitation).
   * Solo lectura: al EDITAR nunca se crea una invitación (eso provocaba el 409
   * "Ya existe una invitación pendiente" permanente). Se puede REENVIAR.
   */
  adminInvitation: BusinessAdminInvitation | null = null;
  adminInvitationLoading = false;
  resendingInvitation = false;

  /**
   * INC 15 — aviso ámbar cuando el alta devolvió `adminInvitationSent: false`:
   * la empresa SÍ se creó, pero el correo de invitación no salió.
   */
  invitationWarning = '';

  readonly identificationTypes = IDENTIFICATION_TYPES;
  allTimezones: string[] = [];
  currencies: CurrencyResponse[] = [];

  logoUrl: string | null = null;
  logoUploading = false;

  private readonly filesApiUrl = `${environment.apiUrl}api/files`;

  constructor(
    private fb: FormBuilder,
    private businessApi: BusinessApiService,
    private businessAdminAuth: BusinessAdminAuthService,
    private invitationApi: InvitationApiService,
    private productoApi: ProductoApiService,
    private datetimeService: DatetimeService,
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient,
    private toast: ToastService
  ) {
    this.allTimezones = this.datetimeService.getSupportedTimezones();
    this.initForm();
  }

  ngOnInit(): void {
    this.loadCurrencies();

    const id = this.route.snapshot.paramMap.get('id');

    if (id) {
      this.isEditing = true;
      this.businessId = Number(id);

      this.disableAdminValidators();
      this.loadBusinessAndAdmin(this.businessId);
      return;
    }

    this.enableInviteValidators();
  }

  private initForm(): void {
  this.form = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(200), this.noOnlyWhitespaceValidator]],

    description: ['', [Validators.maxLength(500)]],
    identificationType: ['', [Validators.maxLength(30)]],
    identificationNumber: ['',[Validators.required,Validators.maxLength(50),this.identificationValidator]],
    address: [''],
    phone: ['', [this.phoneValidator]],

    isActive: [true],
    defaultTimezone: [''],
    defaultCurrencyId: [null],

    adminFullname: [''],
    adminLastname: [''],
    adminMail: ['']
  });
}

  // Catálogo de monedas para el selector "Moneda por defecto". Reutiliza el endpoint
  // GET /api/currencies vía ProductoApiService (el mismo que usa el form de cotización).
  private loadCurrencies(): void {
    this.productoApi.getCurrencies().subscribe({
      next: (currencies) => {
        this.currencies = currencies || [];
      },
      error: () => {
        // El selector queda solo con "— Sin moneda —"; no bloquea el guardado.
        this.currencies = [];
      }
    });
  }

  /** Empresa proveedora del CRM: su estado activo es inmutable. */
  esProveedora = false;

  private loadBusinessAndAdmin(businessId: number): void {
    this.loading = true;
    this.error = '';

    this.businessApi.getById(businessId).pipe(
      switchMap((b: BusinessResponse) => {
        this.logoUrl = b.logoUrl ?? null;
        this.form.patchValue({
          name: b.name ?? '',
          description: b.description ?? '',
          identificationType: b.identificationType ?? '',
          identificationNumber: b.identificationNumber ?? '',
          address: b.address ?? '',
          phone: b.phone ?? '',
          isActive: b.isActive ?? true,
          defaultTimezone: b.defaultTimezone ?? '',
          defaultCurrencyId: b.defaultCurrencyId ?? null
        }, { emitEvent: false });

        // La empresa proveedora no se puede desactivar (el backend responde 409):
        // el toggle queda bloqueado en activo en vez de dejar guardar y fallar.
        this.esProveedora = b.isProvider === true;
        if (this.esProveedora) {
          this.form.get('isActive')?.setValue(true, { emitEvent: false });
          this.form.get('isActive')?.disable({ emitEvent: false });
        }

        // El admin vinculado puede ser Super Admin (roleId 1) o Admin (roleId 2).
        // El endpoint /api/auth/users/search filtra por roleId EXACTO (no acepta lista),
        // así que lanzamos ambas búsquedas en paralelo y priorizamos el Super Admin
        // (rol 1 = dueño/vínculo real) cuando existe, cayendo al Admin (rol 2) si no.
        return forkJoin({
          superAdmin: this.businessAdminAuth.searchBusinessAdmins({ businessId, roleId: 1, page: 0, size: 1 }),
          admin: this.businessAdminAuth.searchBusinessAdmins({ businessId, roleId: 2, page: 0, size: 1 }),
          // INC 15: estado de la invitación de admin. 404 = la empresa no tiene
          // invitación registrada (alta antigua) → simplemente no se muestra el panel.
          invitation: this.businessApi.getAdminInvitation(businessId).pipe(
            catchError(() => of(null as BusinessAdminInvitation | null))
          )
        });

      }),
      finalize(() => (this.loading = false))
    ).subscribe({
      next: ({ superAdmin, admin: adminPage, invitation }) => {
        this.adminInvitation = invitation;
        const admin =
          ((superAdmin?.content ?? [])[0] as BusinessAdminUserResponse | undefined) ??
          ((adminPage?.content ?? [])[0] as BusinessAdminUserResponse | undefined);


        if (admin?.id) {
          this.adminExists = true;
          this.adminUserId = admin.id;
          this.adminRoleId = admin.idRol ?? 2;

          this.enableAdminEditValidators();
          this.form.patchValue({
            adminFullname: admin.fullname ?? '',
            adminLastname: admin.lastname ?? '',
            adminMail: admin.mail ?? ''
          }, { emitEvent: false });
        } else {
          this.adminExists = false;
          this.adminUserId = null;
          this.adminRoleId = null;

          // INC 15: al editar SIN admin vinculado no se pide correo ni se crea
          // invitación — solo se muestra el estado de la invitación existente.
          this.disableAdminValidators();
          this.form.patchValue({
            adminFullname: '',
            adminLastname: '',
            adminMail: invitation?.mailInvited ?? ''
          }, { emitEvent: false });
        }
      },
      error: (err) => {
        this.error = this.extractErrorMessage(err, 'Error al cargar datos');
      }
    });
  }

  /**
   * INC 15: al crear (o al editar un business sin admin vinculado) el admin se
   * incorpora por INVITACIÓN — solo se necesita su correo; el nombre/apellido y la
   * contraseña los define la propia persona al aceptar la invitación.
   */
  private enableInviteValidators(): void {
    this.form.get('adminMail')?.setValidators([Validators.required, Validators.email, Validators.maxLength(200)]);
    ['adminFullname', 'adminLastname'].forEach(k => this.form.get(k)?.clearValidators());
    ['adminFullname', 'adminLastname', 'adminMail'].forEach(k => this.form.get(k)?.updateValueAndValidity());
  }

  /** Editar un admin ya existente: sí se actualizan nombre/apellido/correo. */
  private enableAdminEditValidators(): void {
    this.form.get('adminFullname')?.setValidators([Validators.required, Validators.maxLength(200), this.personNameValidator]);
    this.form.get('adminLastname')?.setValidators([Validators.required, Validators.maxLength(200), this.personNameValidator]);
    this.form.get('adminMail')?.setValidators([Validators.required, Validators.email, Validators.maxLength(200)]);
    ['adminFullname', 'adminLastname', 'adminMail'].forEach(k => this.form.get(k)?.updateValueAndValidity());
  }

  private disableAdminValidators(): void {
    ['adminFullname', 'adminLastname', 'adminMail'].forEach((key) => {
      const c = this.form.get(key);
      c?.clearValidators();
      c?.updateValueAndValidity();
    });
  }

  guardar(): void {
    this.normalizeFormValues();

    if (this.isEditing) {
      if (this.adminExists) this.enableAdminEditValidators();
      else this.disableAdminValidators();
    } else {
      this.enableInviteValidators();
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.error = 'Revisa los campos marcados antes de continuar.';
      return;
    }

    this.saving = true;
    this.error = '';
    this.savingStep = 'idle';

    // INC 1: el campo Color ya no lo edita la UI y NO se envía — el backend
    // preserva el color guardado cuando el campo viene ausente/null.
    const businessPayload: BusinessRequest = {
      name: this.form.value.name,
      description: this.form.value.description || null,
      identificationNumber: this.form.value.identificationNumber,
      identificationType: this.form.value.identificationType || null,
      address: this.form.value.address || null,
      phone: this.form.value.phone || null,
      // getRawValue(): con la proveedora el control va disabled y `form.value` lo omite,
      // lo que mandaría isActive = undefined y el backend lo rechazaría por not-null.
      isActive: this.form.getRawValue().isActive,
      defaultTimezone: this.form.value.defaultTimezone || null,
      defaultCurrencyId: this.form.value.defaultCurrencyId ?? null,
      logoUrl: this.logoUrl || null
    };

    if (this.isEditing) {
      const businessId = this.businessId;
      if (!businessId) {
        this.saving = false;
        this.error = 'BusinessId inválido.';
        return;
      }

      this.savingStep = 'business';

      // INC 15: al EDITAR nunca se manda adminMail ni se crea invitación (el backend
      // lo ignora en PUT). Solo se actualiza el admin YA vinculado, si lo hay.
      this.businessApi.update(businessId, businessPayload).pipe(
        switchMap(() => {
          if (this.adminExists && this.adminUserId) {
            this.savingStep = 'admin';
            const updatePayload: AdminUpdateUserRequest = {
              fullname: this.form.value.adminFullname,
              lastname: this.form.value.adminLastname,
              mail: this.form.value.adminMail,
              businessId: businessId,
              // Preserva el rol existente (no degradar un Super Admin a Admin).
              idRol: this.adminRoleId ?? 2,
              isActive: true
            };
            return this.businessAdminAuth.updateAdminUser(this.adminUserId, updatePayload);
          }
          return of(null);
        }),
        finalize(() => {
          this.saving = false;
          this.savingStep = 'idle';
        })
      ).subscribe({
        next: () => {
          this.toast.updated('Empresa');
          this.router.navigate(['/businesses']);
        },
        error: (err) => {
          const msg = this.extractErrorMessage(err, 'Error al actualizar la empresa y su administrador');
          this.error = msg;
          this.toast.error({ title: 'Error', message: msg });
        }
      });

      return;
    }

    // ── Alta (INC 15): UNA sola llamada. El backend crea la empresa Y la invitación
    //    de admin (rol Admin) en el mismo flujo. Ya NO se llama a POST /api/invitations.
    this.savingStep = 'business';
    const adminMail: string = this.form.value.adminMail;

    this.businessApi.create({ ...businessPayload, adminMail }).pipe(
      finalize(() => {
        this.saving = false;
        this.savingStep = 'idle';
      })
    ).subscribe({
      next: (created: BusinessResponse) => {
        if (created?.adminInvitationSent === false) {
          // La empresa SÍ se creó; el correo no salió. No mentimos sobre el envío:
          // nos quedamos en el formulario (ya en modo edición) con el aviso ámbar
          // y el botón "Reenviar invitación".
          this.toast.success({
            title: 'Empresa creada',
            message: `La empresa "${created.name}" se creó correctamente.`
          });
          this.toast.warning({
            title: 'Invitación no enviada',
            message: created.adminInvitationError?.trim()
              || `No se pudo enviar el correo de invitación a ${adminMail}. Reenvíalo desde esta pantalla.`
          });
          this.switchToEditAfterCreate(created, adminMail);
          return;
        }

        this.toast.success({
          title: 'Empresa creada',
          message: `Enviamos una invitación a ${adminMail} para que el administrador configure su contraseña.`
        });
        this.router.navigate(['/businesses']);
      },
      error: (err) => {
        const msg = this.extractErrorMessage(err, 'Error al registrar la empresa');
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  /**
   * INC 15: tras un alta con `adminInvitationSent: false` la empresa ya existe.
   * Convertimos el formulario a modo edición IN SITU (sin navegar, para no perder el
   * aviso) y cargamos el estado de la invitación para poder reenviarla.
   */
  private switchToEditAfterCreate(created: BusinessResponse, adminMail: string): void {
    this.isEditing = true;
    this.businessId = created.id;
    this.invitationWarning = created.adminInvitationError?.trim()
      || `No se pudo enviar el correo de invitación a ${adminMail}.`;
    this.adminExists = false;
    this.adminUserId = null;
    this.adminRoleId = null;
    this.disableAdminValidators();
    this.loadAdminInvitation(created.id);
  }

  /** GET /api/businesses/{id}/admin-invitation — 404 ⇒ sin invitación registrada. */
  private loadAdminInvitation(businessId: number): void {
    this.adminInvitationLoading = true;
    this.businessApi.getAdminInvitation(businessId).pipe(
      finalize(() => (this.adminInvitationLoading = false))
    ).subscribe({
      next: (inv) => (this.adminInvitation = inv),
      error: () => (this.adminInvitation = null)
    });
  }

  /**
   * INC 15: POST /api/invitations/{id}/resend?businessId={id}. El `businessId` es
   * necesario porque el Super Admin reenvía la invitación de OTRA empresa.
   */
  reenviarInvitacion(): void {
    const invitation = this.adminInvitation;
    const businessId = this.businessId;
    if (!invitation?.id || !businessId || this.resendingInvitation) return;

    this.resendingInvitation = true;
    this.invitationApi.resendInvitation(invitation.id, businessId).pipe(
      finalize(() => (this.resendingInvitation = false))
    ).subscribe({
      next: (res) => {
        this.invitationWarning = '';
        this.adminInvitation = {
          ...invitation,
          status: res?.status ?? invitation.status,
          expiresAt: res?.expiresAt ?? invitation.expiresAt,
          invitationLink: res?.invitationLink ?? invitation.invitationLink
        };
        this.toast.success({
          title: 'Invitación reenviada',
          message: `Enviamos nuevamente la invitación a ${invitation.mailInvited}.`
        });
      },
      error: (err) => {
        this.toast.error({
          title: 'Error',
          message: this.extractErrorMessage(err, 'No se pudo reenviar la invitación.')
        });
      }
    });
  }

  /** Texto del estado de la invitación de admin para el panel de edición. */
  get adminInvitationLabel(): string {
    const inv = this.adminInvitation;
    if (!inv) return '';
    switch (inv.status) {
      case 'PENDING': return `Invitación pendiente para ${inv.mailInvited}`;
      case 'ACCEPTED': return `Invitación aceptada por ${inv.mailInvited}`;
      case 'EXPIRED': return `Invitación expirada para ${inv.mailInvited}`;
      case 'REVOKED': return `Invitación revocada para ${inv.mailInvited}`;
      default: return `Invitación para ${inv.mailInvited}`;
    }
  }

  /** Solo tiene sentido reenviar invitaciones PENDING o EXPIRED. */
  get canResendInvitation(): boolean {
    const status = this.adminInvitation?.status;
    return status === 'PENDING' || status === 'EXPIRED';
  }

  formatFecha(value?: string | null): string {
    return this.datetimeService.formatDateTime(value);
  }

  // Formatos aceptados por el backend para COMPANY_LOGO (SVG NO está soportado).
  private readonly LOGO_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  private readonly LOGO_MAX_BYTES = 10 * 1024 * 1024; // 10 MB

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    // Validación en cliente ANTES de subir, para no mandar archivos que el backend rechazará.
    if (!this.LOGO_ALLOWED_TYPES.includes(file.type)) {
      const msg = 'Formato de imagen no soportado. Usa JPEG, PNG, GIF o WebP (SVG no se admite).';
      this.error = msg;
      this.toast.error({ title: 'Logo no válido', message: msg });
      input.value = '';
      return;
    }

    if (file.size > this.LOGO_MAX_BYTES) {
      const msg = 'La imagen supera el tamaño máximo de 10 MB. Sube un archivo más liviano.';
      this.error = msg;
      this.toast.error({ title: 'Logo demasiado grande', message: msg });
      input.value = '';
      return;
    }

    this.error = '';
    this.logoUploading = true;
    const formData = new FormData();
    formData.append('file', file);

    this.http.post<{ publicUrl?: string }>(
      `${this.filesApiUrl}/upload?category=COMPANY_LOGO&isPublic=true`,
      formData
    ).pipe(
      finalize(() => (this.logoUploading = false))
    ).subscribe({
      next: (res) => {
        input.value = '';
        if (!res?.publicUrl) {
          const msg = 'El servidor no devolvió la URL del logo. Intenta nuevamente.';
          this.error = msg;
          this.toast.error({ title: 'Error al subir el logo', message: msg });
          return;
        }
        this.logoUrl = res.publicUrl;
      },
      error: (err) => {
        // Surface el mensaje real del backend (p. ej. "Invalid file type: ... for category: ...").
        // El backend lo manda en err.error.message; extractErrorMessage para 400 devuelve un
        // genérico, así que priorizamos el mensaje crudo del backend cuando viene.
        const backendMsg =
          (typeof err?.error?.message === 'string' && err.error.message) ||
          (typeof err?.error === 'string' && err.error) ||
          '';
        const msg = backendMsg || this.extractErrorMessage(err, 'No se pudo subir el logo. Intenta nuevamente.');
        this.error = msg;
        this.toast.error({ title: 'Error al subir el logo', message: msg });
        input.value = '';
      }
    });
  }

  removeLogo(): void {
    this.logoUrl = null;
  }

  cancelar(): void {
    this.router.navigate(['/businesses']);
  }

  hasError(field: string): boolean {
    const control = this.form.get(field);
    return control ? control.invalid && (control.touched || control.dirty) : false;
  }

  getError(field: string): string {
    const control = this.form.get(field);
    if (!control || !control.errors) return '';

    if (control.errors['required']) return 'Este campo es requerido';
    if (control.errors['email']) return 'Correo inválido';
    if (control.errors['minlength']) return `Mínimo ${control.errors['minlength'].requiredLength} caracteres`;
    if (control.errors['maxlength']) return `Máximo ${control.errors['maxlength'].requiredLength} caracteres`;

    if (control.errors['whitespace']) return 'No puede estar vacío';
    if (control.errors['invalidIdentification']) return 'Identificación inválida (solo letras/números y guiones)';
    if (control.errors['invalidPhone']) return 'Teléfono inválido: usa dígitos, espacios, guiones o paréntesis, con un "+" inicial opcional (6 a 20 caracteres)';
    if (control.errors['invalidName']) return 'Solo letras y espacios';
    if (control.errors['weakPassword']) return 'Usa 8+ caracteres con mayúscula, minúscula y número';

    return 'Campo inválido';
  }

  private normalizeFormValues(): void {
    const patch: any = {};

    patch.name = String(this.form.value.name ?? '').trim();
    patch.description = String(this.form.value.description ?? '').trim();
    patch.address = String(this.form.value.address ?? '').trim();

    const idn = String(this.form.value.identificationNumber ?? '').trim();
    patch.identificationNumber = idn.toUpperCase();

    patch.phone = String(this.form.value.phone ?? '').trim();

    patch.adminFullname = String(this.form.value.adminFullname ?? '').trim();
    patch.adminLastname = String(this.form.value.adminLastname ?? '').trim();
    patch.adminMail = String(this.form.value.adminMail ?? '').trim().toLowerCase();

    this.form.patchValue(patch, { emitEvent: false });
  }

  private extractErrorMessage(err: any, fallback: string): string {
    const raw =
      err?.error?.message ||
      err?.error?.error ||
      err?.error?.detail ||
      (typeof err?.error === 'string' ? err.error : '') ||
      err?.message ||
      '';

    const msg = String(raw).toLowerCase();
    const status = err?.status;

    if (msg.includes('23505') || msg.includes('duplicate') || msg.includes('llave duplicada')) {
      if (msg.includes('identification') || msg.includes('identification_number') || msg.includes('identificationnumber') || msg.includes('bussines_identification_number')) {
        return 'La identificación (RUC/NIF) ya está registrada. Usa otra.';
      }
      if (msg.includes('mail') || msg.includes('email')) {
        return 'El correo del administrador ya está registrado. Usa otro.';
      }
      return 'Ya existe un registro con esos datos. Revisa e intenta otra vez.';
    }

    if (status === 0) return 'No se pudo conectar al servidor. Revisa tu conexión.';
    if (status === 400) return 'Revisa los datos ingresados. Hay campos inválidos.';
    if (status === 401) return 'Tu sesión expiró. Vuelve a iniciar sesión.';
    if (status === 403) return 'No tienes permisos para realizar esta acción.';
    if (status === 404) return 'No se encontró el recurso solicitado.';
    if (status >= 500) return 'Ocurrió un error en el servidor. Intenta nuevamente.';

    return raw || fallback;
  }

  private noOnlyWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
    const v = String(control.value ?? '');
    if (!v.trim()) return { whitespace: true };
    return null;
  }

  private noOnlyWhitespaceOptionalValidator(control: AbstractControl): ValidationErrors | null {
    const v = String(control.value ?? '');
    if (v.length > 0 && !v.trim()) return { whitespace: true };
    return null;
  }

  private identificationValidator(control: AbstractControl): ValidationErrors | null {
    const v = String(control.value ?? '').trim();
    if (!v) return null;
    if (v.length < 6) return { invalidIdentification: true };
    if (!/^[A-Za-z0-9-]+$/.test(v)) return { invalidIdentification: true };
    return null;
  }

  private phoneValidator(control: AbstractControl): ValidationErrors | null {
    const v = String(control.value ?? '').trim();
    if (!v) return null;
    // Acepta formatos internacionales razonables (dígitos, espacios, guiones, paréntesis,
    // '+' inicial opcional), 6-20 caracteres con al menos 6 dígitos. Debe calzar con el
    // @Pattern de BusinessRequest.phone en el backend — el dato semilla real
    // ("+51-555-0100") debe poder re-guardarse sin cambios sin fallar esta validación.
    if (!/^(?=(?:.*\d){6,})\+?[0-9\s()-]{6,20}$/.test(v)) return { invalidPhone: true };
    return null;
  }

  private personNameValidator(control: AbstractControl): ValidationErrors | null {
    const v = String(control.value ?? '').trim();
    if (!v) return null;
    if (!/^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ\s'.-]+$/.test(v)) return { invalidName: true };
    return null;
  }
}